import { KeyNodeType } from '@overtur-monorepo/keying-shared'
import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Subject, takeUntil } from 'rxjs'
import { flatMap, take } from 'rxjs/operators'
import { KeySystemNavConfig } from '../../configuration/key.system.nav.config'
import { KeySystem, Structure } from '../../models/dev.test.models'
import { ErrorService } from '../../services/error.service'
import { MasterKeyService } from '../../services/keying.masterkey.service'
import { KeyingStructureService } from '../../services/keying.structure.service'
import { KeyingSystemService } from '../../services/keying.system.service'

@Component({
    selector: 'app-cross-key-dialog',
    templateUrl: './cross-key-dialog.component.html',
    styleUrls: ['./cross-key-dialog.component.scss'],
    standalone: false
})
export class CrossKeyDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  keyNodeType = KeyNodeType

  crossKeyForm: FormGroup
  structure: Structure
  keySystem: KeySystem

  lastCrossKey
  crossData

  skipErrors = false

  focus = {
    name: false,
  }

  public errorMessages = {
    name: [
      { type: 'required', message: 'Name is required.' },
      { type: 'duplicateName', message: 'There is already a cross keyed cylinder with this name.' },
      { type: 'notXey', message: 'Cross keyed cylinder names must start with X.' },
      { type: 'unavailableKeyName', message: 'Must contain an existing key symbol or be a number.' },
    ],
  }

  constructor(
    public form: FormBuilder,
    private dialogRef: MatDialogRef<CrossKeyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private errorService: ErrorService,
    private masterKeyService: MasterKeyService
  ) {
    this.crossData = data
    this.crossKeyForm = this.form.group({
      name: new FormControl('', [
        Validators.required,
        Validators.maxLength(100),
        this.duplicateNameValidator,
        this.beginsWithXValidator,
        this.keyOrNumberValidator,
      ]),
    })
  }

  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  ngOnInit() {
    this.keyingStructureService.structure$
      .pipe(
        takeUntil(this.destroy$),
        flatMap((structure: Structure) => {
          this.structure = structure
          return this.keyingSystemService.keySystem$
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (keySystem: KeySystem) => {
          this.keySystem = keySystem
          this.getLastCrossKey()
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  getLastCrossKey() {
    this.masterKeyService
      .getLastCrossKey(this.structure.id, this.keySystem.id)
      .pipe(take(1))
      .subscribe((lastCrossKey) => {
        this.lastCrossKey = lastCrossKey
      })
  }

  // TODO
  // getLastSKD() {
  //   this.masterKeyService.getLastSKD(this.structure.id, this.keySystem.id)
  //   .pipe(take(1))
  //   .subscribe(lastSKD => {
  //     console.log('lastSKD', lastSKD)
  //   })
  // }

  duplicateNameValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '') {
      let crossKeyMaster = this.keySystem.masterKeysKeying.children.find(
        (k) => k.masterKeyTypeId === this.keyNodeType.CrossKeyMaster
      )
      let crossKeys = crossKeyMaster.children
      const duplicateName = crossKeys.some((x) => x.keySetSymbol.toUpperCase() === value.toUpperCase())
      if (duplicateName) {
        return { duplicateName, type: 'duplicateName' }
      }
    }
    return null
  }

  beginsWithXValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '') {
      const notXey = value.charAt(0).toUpperCase() !== 'X'
      if (notXey) {
        return { notXey, type: 'notXey' }
      }
    }
    return null
  }

  keyOrNumberValidator = (control: FormControl) => {
    const value = control.value.toUpperCase()

    if (value !== '' && value !== null) {
      const lastChar = value.charAt(value.length - 1)
      const keyName = value.replace(/X/g, '')

      const keys = this.keySystem.masterKeysKeying.squashIncludeChangeKeys()
      const nameFound = keys.some((x) => x.keySetSymbol === keyName)

      // Does not end with 'X'
      if (lastChar !== 'X') {
        // Must be an existing key
        if (!nameFound) {
          const unavailableKeyName = true
          return { unavailableKeyName, type: 'unavailableKeyName' }
        }
        // Ends with 'X'
      } else if (lastChar === 'X') {
        const asNumber = Number(keyName)
        const isNumber = !isNaN(asNumber)

        // Must be a number
        if (!isNumber && !nameFound) {
          const unavailableKeyName = true
          return { unavailableKeyName, type: 'unavailableKeyName' }
        }
      }
    }
    return null
  }

  /* istanbul ignore next */
  displayName(subject) {
    if (subject.name) {
      return (subject && subject.name) || subject
    } else if (subject.description) {
      return (subject && subject.description) || subject
    }
  }

  /* istanbul ignore next */
  create() {
    if (this.crossKeyForm.valid) {
      const data = {
        masterKey: this.crossData.masterKey,
        action: this.crossData.action,
        name: this.crossKeyForm.controls.name.value,
      }

      this.dialogRef.close(data)
    }
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  close() {
    this.dialogRef.close()
  }
}
